import React from 'react';
import { ToolResultList } from './ToolResultList';
import { ToolResultSingular } from './ToolResultSingular';
import { FieldValue } from './ToolResultField';
import { AlertCircle } from 'lucide-react';

interface ToolResultItem {
  values: FieldValue[];
}

interface FormattedResponse {
  mode: 'list' | 'singular' | 'single';
  items?: ToolResultItem[];
  values?: FieldValue[];
}

interface ToolResultCardProps {
  tool_name: string;
  action_name: string;
  formatted_response: FormattedResponse;
  onLinkClick?: (href: string) => void;
}

const ensureDisplayString = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  if (value && typeof value === 'object') {
    const candidate =
      (value as any).name ??
      (value as any).action_name ??
      (value as any).display_name ??
      (value as any).title;

    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  if (value !== undefined && value !== null) {
    return String(value);
  }

  return fallback;
};

/**
 * Main container for tool execution results
 * Routes to appropriate renderer based on response mode
 */
export const ToolResultCard: React.FC<ToolResultCardProps> = ({
  tool_name,
  action_name,
  formatted_response,
  onLinkClick
}) => {
  const safeToolName = ensureDisplayString(tool_name, 'Tool');
  const safeActionName = ensureDisplayString(action_name, 'Action');

  console.log('🔧 [ToolResultCard] Rendering tool result:', {
    tool_name: safeToolName,
    action_name: safeActionName,
    mode: formatted_response?.mode,
    has_items: !!formatted_response?.items,
    has_values: !!formatted_response?.values
  });

  // Validate formatted_response exists
  if (!formatted_response) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800 mb-1">
              Response Format Not Supported
            </p>
            <p className="text-xs text-yellow-700">
              Tool executed successfully but the response format is not recognized.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { mode, items, values } = formatted_response;

  // Handle list mode
  if (mode === 'list') {
    if (!items || items.length === 0) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
          <div className="text-4xl mb-2">📭</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Results</h3>
          <p className="text-gray-600">No items found for this query.</p>
        </div>
      );
    }

    return (
      <ToolResultList items={items} tool_name={safeToolName} action_name={safeActionName} onLinkClick={onLinkClick} />
    );
  }

  // Handle singular/single mode
  if (mode === 'singular' || mode === 'single') {
    // For singular mode, data can be in 'values' or in 'items[0].values'
    const singularValues = values || (items && items.length > 0 ? items[0].values : null);

    if (!singularValues || singularValues.length === 0) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">No data available</p>
        </div>
      );
    }

    return (
      <ToolResultSingular
        values={singularValues}
        tool_name={safeToolName}
        action_name={safeActionName}
        onLinkClick={onLinkClick}
      />
    );
  }

  // Unknown mode fallback
  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-yellow-800 mb-1">
            Unknown Response Mode: {mode}
          </p>
          <p className="text-xs text-yellow-700">
            The response mode "{mode}" is not supported. Expected "list", "singular", or "single".
          </p>
        </div>
      </div>
    </div>
  );
};
